import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Grid,
  Typography,
  Divider,
  Button,
  CircularProgress,
  Chip,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Snackbar,
} from '@mui/material';
import {
  Edit as EditIcon,
  ArrowBack as ArrowBackIcon,
  VpnKey as LicenseIcon,
} from '@mui/icons-material';
import { useAuth } from '@/context/AuthContext';
import PageHeader from '@/components/common/PageHeader';
import StatusChip from '@/components/common/StatusChip';
import { licensesApi, License, Subscription, Product } from '@/api/licenses-api';
import { usersApi, Customer } from '@/api/users-api';

export default function LicenseDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [license, setLicense] = useState<License | null>(null);
  const [subscription, setSubscription] = useState<Subscription | null>(null);
  const [product, setProduct] = useState<Product | null>(null);
  const [customer, setCustomer] = useState<Customer | null>(null);
  const [loading, setLoading] = useState(true);
  const [openEditDialog, setOpenEditDialog] = useState(false);
  const [formData, setFormData] = useState({
    status: '',
    expirationDate: '',
  });
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  const fetchLicenseData = async () => {
    if (!id) return;
    
    setLoading(true);
    setError(null);
    
    try {
      // Fetch license details
      const licenseData = await licensesApi.getLicense(id);
      setLicense(licenseData);
      setFormData({
        status: licenseData.status,
        expirationDate: new Date(licenseData.expirationDate).toISOString().split('T')[0],
      });
      
      // Fetch subscription details
      const subscriptionData = await licensesApi.getSubscription(licenseData.subscriptionId);
      setSubscription(subscriptionData);
      
      // Fetch product details
      const productData = await licensesApi.getProduct(subscriptionData.productId);
      setProduct(productData);
      
      // Fetch customer details
      const customerData = await usersApi.getCustomer(subscriptionData.customerId);
      setCustomer(customerData);
    } catch (err) {
      console.error('Error fetching license data:', err);
      setError('Failed to load license data. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLicenseData();
  }, [id]);

  const handleEditLicense = async () => {
    if (!license) return;
    
    try {
      await licensesApi.updateLicense(license.licenseId, {
        status: formData.status as 'issued' | 'active' | 'expired',
        expirationDate: formData.expirationDate,
      });
      
      setOpenEditDialog(false);
      setSuccessMessage('License updated successfully');
      fetchLicenseData();
    } catch (err) {
      console.error('Error updating license:', err);
      setError('Failed to update license. Please try again.');
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ mt: 4 }}>
        <Alert severity="error">{error}</Alert>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/licenses')}
          sx={{ mt: 2 }}
        >
          Back to Licenses
        </Button>
      </Box>
    );
  }

  if (!license || !subscription || !product || !customer) {
    return (
      <Box sx={{ mt: 4 }}>
        <Alert severity="warning">License not found.</Alert>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/licenses')}
          sx={{ mt: 2 }}
        >
          Back to Licenses
        </Button>
      </Box>
    );
  }

  // Calculate days remaining
  const today = new Date();
  const expirationDate = new Date(license.expirationDate);
  const daysRemaining = Math.ceil((expirationDate.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
  
  // Calculate if license is expiring soon (within 30 days)
  const isExpiringSoon = daysRemaining > 0 && daysRemaining <= 30 && license.status === 'active';
  
  // Calculate if license is expired
  const isExpired = daysRemaining <= 0 || license.status === 'expired';

  return (
    <>
      <PageHeader
        title={`License: ${license.licenseKey}`}
        breadcrumbs={[
          { label: 'Dashboard', href: '/dashboard' },
          { label: 'Licenses', href: '/licenses' },
          { label: `License ${license.licenseKey.substring(0, 8)}...` },
        ]}
        action={
          user?.role !== 'customer' ? {
            label: 'Edit License',
            onClick: () => setOpenEditDialog(true),
            icon: <EditIcon />,
          } : undefined
        }
      />

      <Card sx={{ mb: 4 }}>
        <CardContent>
          <Grid container spacing={3}>
            <Grid size={{ xs: 12, md: 7}}>
              <Box sx={{ mb: 3 }}>
                <Grid container spacing={2}>
                  <Grid size={{ xs: 12}}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <LicenseIcon sx={{ mr: 1, color: 'primary.main' }} />
                        <Typography variant="h6">License Details</Typography>
                      </Box>
                      <StatusChip status={license.status} type="license" size="medium" />
                    </Box>
                    <Divider sx={{ my: 2 }} />
                  </Grid>
                  
                  <Grid size={{ xs: 4}}>
                    <Typography variant="body2" color="text.secondary">
                      License Key
                    </Typography>
                  </Grid>
                  <Grid size={{ xs: 8}}>
                    <Typography variant="body1" sx={{ wordBreak: 'break-all' }}>
                      {license.licenseKey}
                    </Typography>
                  </Grid>

                  <Grid size={{ xs: 4}}>
                    <Typography variant="body2" color="text.secondary">
                      Issue Date
                    </Typography>
                  </Grid>
                  <Grid size={{ xs: 8}}>
                    <Typography variant="body1">
                      {new Date(license.issueDate).toLocaleDateString()}
                    </Typography>
                  </Grid>

                  <Grid size={{ xs: 4}}>
                    <Typography variant="body2" color="text.secondary">
                      Expiration Date
                    </Typography>
                  </Grid>
                  <Grid size={{ xs: 8}}>
                    <Typography variant="body1">
                      {new Date(license.expirationDate).toLocaleDateString()}
                      {isExpiringSoon && (
                        <Chip 
                          label={`Expires in ${daysRemaining} days`} 
                          color="warning" 
                          size="small" 
                          sx={{ ml: 1 }}
                        />
                      )}
                      {isExpired && (
                        <Chip 
                          label="Expired" 
                          color="error" 
                          size="small" 
                          sx={{ ml: 1 }}
                        />
                      )}
                    </Typography>
                  </Grid>

                  <Grid size={{ xs: 12}}>
                    <Divider sx={{ my: 2 }} />
                    <Typography variant="h6">Related Information</Typography>
                    <Divider sx={{ my: 2 }} />
                  </Grid>

                  <Grid size={{ xs: 4}}>
                    <Typography variant="body2" color="text.secondary">
                      Customer
                    </Typography>
                  </Grid>
                  <Grid size={{ xs: 8}}>
                    <Typography variant="body1">
                      {customer.name}
                      <Button 
                        size="small" 
                        sx={{ ml: 1 }}
                        onClick={() => navigate(`/customers/${customer.customerId}`)}
                      >
                        View
                      </Button>
                    </Typography>
                  </Grid>

                  <Grid size={{ xs: 4}}>
                    <Typography variant="body2" color="text.secondary">
                      Subscription
                    </Typography>
                  </Grid>
                  <Grid size={{ xs: 8}}>
                    <Typography variant="body1">
                      {subscription.chargebeeSubscriptionId.substring(0, 8)}...
                      <Button 
                        size="small" 
                        sx={{ ml: 1 }}
                        onClick={() => navigate(`/subscriptions/${subscription.subscriptionId}`)}
                      >
                        View
                      </Button>
                    </Typography>
                  </Grid>

                  <Grid size={{ xs: 4}}>
                    <Typography variant="body2" color="text.secondary">
                      Product
                    </Typography>
                  </Grid>
                  <Grid size={{ xs: 8}}>
                    <Typography variant="body1">{product.description}</Typography>
                  </Grid>
                </Grid>
              </Box>
            </Grid>
            
            <Grid size={{ xs: 12, md: 5}}>
              <Card sx={{ mb: 3, bgcolor: 'background.default' }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom>License Metadata</Typography>
                  
                  {license.metadata && Object.keys(license.metadata).length > 0 ? (
                    <Box component="pre" sx={{ 
                      overflowX: 'auto', 
                      backgroundColor: '#f5f5f5', 
                      p: 2, 
                      borderRadius: 1,
                      fontSize: '0.875rem'
                    }}>
                      {JSON.stringify(license.metadata, null, 2)}
                    </Box>
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      No metadata available for this license.
                    </Typography>
                  )}
                </CardContent>
              </Card>
              
              {license.status === 'expired' && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  This license has expired and is no longer valid.
                </Alert>
              )}
              
              {isExpiringSoon && (
                <Alert severity="warning" sx={{ mb: 2 }}>
                  This license will expire soon. Consider renewing the subscription.
                </Alert>
              )}
              
              {license.status === 'issued' && (
                <Alert severity="info" sx={{ mb: 2 }}>
                  This license has been issued but is not yet active.
                </Alert>
              )}
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Edit License Dialog */}
      <Dialog
        open={openEditDialog}
        onClose={() => setOpenEditDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Edit License</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1, pb: 2 }}>
            <TextField
              select
              fullWidth
              margin="normal"
              label="Status"
              value={formData.status}
              onChange={(e) => setFormData({ ...formData, status: e.target.value })}
              required
            >
              <MenuItem value="issued">Issued</MenuItem>
              <MenuItem value="active">Active</MenuItem>
              <MenuItem value="expired">Expired</MenuItem>
            </TextField>
            
            <TextField
              fullWidth
              margin="normal"
              label="Expiration Date"
              type="date"
              value={formData.expirationDate}
              onChange={(e) => setFormData({ ...formData, expirationDate: e.target.value })}
              required
              InputLabelProps={{ shrink: true }}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenEditDialog(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleEditLicense}
            disabled={!formData.status || !formData.expirationDate}
          >
            Save Changes
          </Button>
        </DialogActions>
      </Dialog>

      {/* Success notification */}
      <Snackbar 
        open={!!successMessage} 
        autoHideDuration={6000} 
        onClose={() => setSuccessMessage(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={() => setSuccessMessage(null)} severity="success" sx={{ width: '100%' }}>
          {successMessage}
        </Alert>
      </Snackbar>
    </>
  );
}
